import { PayPalScriptProvider } from "@paypal/react-paypal-js"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { type FC, type ReactNode, useEffect, useState } from "react"
import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router"
import { ThemeProvider } from "styled-components"

import "@forento/shared-client/assets/editorjs.css"
import "@forento/shared-client/assets/lexical.css"
import { combineContextProviders } from "@forento/shared-client/utilities/component"
import { setAbortableInterval } from "@forento/shared/utilities/general"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import appRoutes from "~/appRoutes"
import { AlertContextProvider } from "~/contexts/AlertContext"
import { PlatformContextProvider, usePlatform } from "~/contexts/PlatformContext"
import { ThemeComponentProvider } from "~/contexts/ThemeComponentContext"
import { UserContextProvider, useUser } from "~/contexts/UserContext"
import { Styles as OriginalStyles } from "~/themes/original/components/Layout"
import { Styles as SchoolStyles } from "~/themes/school/components/Layout"
import "~/translations"
import { changeLanguage } from "~/translations"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import "~/assets/styles.css"

const AppThemeProvider: FC<{ children: ReactNode }> = ({ children }) => (
	<ThemeProvider theme={{ primaryColor: usePlatform().platform.primaryColor.value }}>{children}</ThemeProvider>
)

const AppContextProviders = combineContextProviders([
	BrowserRouter,
	UserContextProvider,
	PlatformContextProvider,
	AppThemeProvider,
	ThemeComponentProvider,
	AlertContextProvider,
])

const App: FC = () => {
	const [queryClient] = useState(() => new QueryClient())

	return (
		<query.Provider client={trpc} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<AppContextProviders>
					<AppContent />
				</AppContextProviders>
			</QueryClientProvider>
		</query.Provider>
	)
}

export default App

const AppContent: FC = () => {
	const platform = usePlatform().platform

	const content = (
		<Routes>
			{appRoutes.map(route => (
				<Route key={route.path} path={route.path} element={<AppRoute route={route} />} />
			))}
		</Routes>
	)

	if (platform.paymentGateway?.type !== "paypal") return content

	return (
		<PayPalScriptProvider
			options={{
				clientId: CONFIG.payPal.clientId,
				merchantId: platform.paymentGateway.id,
				dataPartnerAttributionId: CONFIG.payPal.bnCode,
				currency: platform.currency,
			}}
		>
			{content}
		</PayPalScriptProvider>
	)
}

const AppRoute: FC<{ route: (typeof appRoutes)[number] }> = ({ route }) => {
	const platform = usePlatform().platform
	const Page = route.page[platform.theme.id]

	useEffect(() => {
		changeLanguage(platform.language)
	}, [platform.language])

	return (
		<Content
			path={route.path}
			isRouteAuthorized={route.auth ?? false}
			isCommunityProfileRequired={route.community ?? false}
		>
			{platform.theme.id === "legacy" ? (
				<OriginalStyles />
			) : platform.theme.id === "modern" ? (
				<SchoolStyles />
			) : (
				exhaustiveGuard(platform.theme)
			)}
			<Page />
		</Content>
	)
}

type ContentProps = {
	path?: string
	isRouteAuthorized: boolean
	isCommunityProfileRequired: boolean
	children: ReactNode
}
const Content: FC<ContentProps> = ({ path, isRouteAuthorized, isCommunityProfileRequired, children }) => {
	const location = useLocation()
	const user = useUser()
	const [searchParams] = useSearchParams()

	useEffect(() => {
		window.scrollTo({ left: 0, top: 0, behavior: "smooth" })
	}, [location.pathname])

	useEffect(() => {
		if (user.user?.id === undefined) return

		const abortController = new AbortController()

		let activeDuration = 0
		let backgroundDuration = 0
		trpc.activity.createSession.mutate().then(session => {
			setAbortableInterval(1_000, abortController.signal, () => {
				if (window.document.hidden) backgroundDuration++
				else activeDuration++
			})
			setAbortableInterval(10_000, abortController.signal, () => {
				trpc.activity.updateSession
					.mutate({ id: session.id, activeDuration, backgroundDuration })
					.catch(() => {})
			})
		})

		return () => {
			abortController.abort()
		}
	}, [user.user?.id])

	if (isRouteAuthorized && !user.user) {
		const prefilledEmail = searchParams.get("email") ?? undefined
		const params = new URLSearchParams({
			...(path !== undefined ? { next: location.pathname } : {}),
			...(prefilledEmail !== undefined ? { email: prefilledEmail } : {}),
		})
		const redirect =
			prefilledEmail !== undefined ? `${routes.user.signin()}?${params}` : `${routes.user.signup()}?${params}`

		return <Navigate to={redirect} replace />
	}

	if (isCommunityProfileRequired && user.user && user.user.communityProfileId === null)
		return (
			<Navigate
				to={`${routes.community.create()}${
					path !== undefined ? `?next=${encodeURIComponent(location.pathname)}` : ""
				}`}
				replace
			/>
		)

	return children
}
